import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Table } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { companyID } from "./../../../auth";
import { useTranslation } from "react-i18next";
import FilterComponent from "../../../components/FilterComponent";
import TooltipWrapper from "../../../utils/TooltipWrapper";
import { getPerformanceBonusByEmpId } from "../../../services/performanceBonusServices";
import { formatDateForSQL } from "../../../utils/formatDateForSQL";
import CreatePerformaceBonus from "../../../components/modelpopup/CreatePerformaceBonus";
import Swal from "sweetalert2";
import { decrypt } from "../../../utils/crypto";

const PerformanceBonus = () => {
  const { t } = useTranslation();
  let { employee_id } = useParams();
  employee_id = decrypt(employee_id);
  const location = useLocation();
  const employeeDetails = location.state?.employee;

  const [paginationValue, setPaginationValue] = useState(10);
  const [employeeData, setEmployeeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [filterOptions, setFilterOptions] = useState([
    {
      key: "Pending",
      label: "release_status",
      value: [0],
      category: "PB release status",
    },
    {
      key: "Released",
      label: "release_status",
      value: [1],
      category: "PB release status",
    },
    {
      key: "All",
      label: "release_status",
      value: null,
      category: "PB release status",
    },
  ]);
  const [openPerformanceBonus, setOpenPerformanceBonus] = useState(false);

  const fetchPerformanceBonus = async (params = {}) => {
    try {
      let finalParams = { ...params };

      const queryParams = new URLSearchParams();
      Object.entries(finalParams).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val !== null && val !== undefined && val !== "") {
              queryParams.append(key, val);
            }
          });
        } else {
          queryParams.append(key, value);
        }
      });

      const response = await getPerformanceBonusByEmpId(
        employee_id,
        queryParams
      );

      if (!Array.isArray(response?.data)) {
        console.error("Invalid data format received:", response);
        setEmployeeData([]);
        return;
      }

      setEmployeeData(response.data ?? []);
    } catch (error) {
      console.error("Error fetching company employees:", error);
      setEmployeeData([]);
    } finally {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    if (employee_id) {
      fetchPerformanceBonus();
    }
  }, [openPerformanceBonus]);

  const columns = [
    {
      title: t("Release For"),
      render: (text, record) => `${record.from_month} to ${record.to_month}`,
    },
    {
      title: t("Pb deducted amount"),
      dataIndex: "pb_deducted_amount",
    },
    {
      title: t("Release percentage (%)"),
      dataIndex: "release_percentage",
    },
    {
      title: t("Release amount"),
      dataIndex: "released_amount",
    },
    {
      title: t("Release date"),
      dataIndex: "release_date",
      render: (text) => formatDateForSQL(text),
    },
    {
      title: t("Release by"),
      dataIndex: "released_by",
      render: (_, record) =>
        `${record?.releaser_first_name}${
          record?.releaser_middle_name
            ? " " + record?.releaser_middle_name + " "
            : " "
        }${record?.releaser_last_name}`,
    },
    {
      title: t("Remark"),
      dataIndex: "remarks",
      ellipsis: true,
      width: "20%",
      className: "remark-column",
      render: (text) => (
        <span
          title={text}
          className="remark-text"
          onClick={() =>
            Swal.fire({
              title: "PB Remark",
              text: text,
              confirmButtonText: "Close",
            })
          }
        >
          {text.length > 40 ? `${text.slice(0, 50)}...` : text}
          {text.length > 40 && (
            <span style={{ color: "blue", cursor: "pointer" }}> Read more</span>
          )}
        </span>
      ),
    },
    {
      title: t("Release status"),
      render: (text, record) => {
        const status = record.release_status == "1";
        return (
          <span className={`badge bg-${status ? "success" : "warning"}`}>
            {status ? t("Released") : t("Pending")}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    const startIndex = (currentPage - 1) * paginationValue;
    const endIndex = startIndex + paginationValue;
    const employees = employeeData?.slice(startIndex, endIndex);

    setCurrentData(employees);
  }, [employeeData, currentPage]);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={
            employeeDetails
              ? `${employeeDetails?.first_name} ${employeeDetails?.middle_name}
              ${employeeDetails?.last_name}`
              : t("Performance Bonus")
          }
          title={t("Dashboard")}
          subtitle={t("Performance Bonus")}
          name={t("Release PB")}
          openModal={() => setOpenPerformanceBonus(true)}
          page="performance-bonus/:employee_id"
          action="create"
        />
        {/* <div className="d-flex justify-content-end mb-2">
          <TooltipWrapper tooltipText="Filter">
            <FilterComponent
              filterOptions={filterOptions}
              onApplyFilter={(selectedFilter) => {
                const queryParams = Object.entries(selectedFilter)?.length
                  ? selectedFilter
                  : {
                      release_status: [0],
                    };
                fetchPerformanceBonus(queryParams);
              }}
              filterLabel="Filter Employees"
              resetLabel="Reset Filter"
              applyLabel="Apply"
            />
          </TooltipWrapper>
        </div> */}

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={currentData}
            rowKey={(record) => record.id}
            pagination={{ pageSize: paginationValue }}
            scroll={{ x: true }}
            tableLayout="auto"
          />
        </div>
      </div>
      {openPerformanceBonus && (
        <CreatePerformaceBonus
          show={openPerformanceBonus}
          handleClose={() => setOpenPerformanceBonus(false)}
          employee={employee_id}
        />
      )}
    </div>
  );
};

export default PerformanceBonus;
